import { useState, useMemo } from 'react';
import { KeyRound } from 'lucide-react';
import { useI18n, useNamespace } from '../context/I18nContext';
import { apiClient, ApiError } from '../shared/lib/apiClient';
import { navigate } from '../shared/lib/router';

function useResetToken() {
  return useMemo(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('token') || '';
  }, []);
}

// This page loads with no session and (on local dev) no real subdomain to
// resolve a tenant from, so the invite/reset email links carry it explicitly
// (see administration/routes.js's inviteUrl and core/routes.js's resetUrl).
// Sent back as `tenantSlug` in the request body, which tenant-resolver.js
// prioritizes over the generic X-Tenant-Slug dev header apiClient attaches
// to every request -- without this, that header (whatever tenant was last
// guessed, almost never this one) wins instead and the request 404s.
function useTenantSlugParam() {
  return useMemo(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('tenant') || '';
  }, []);
}

// The forced 180-day renewal reuses this same page/endpoint (see
// password-policy.js's checkPasswordExpiryChallenge) since it's the same
// underlying action as a normal reset -- only the copy differs, signaled via
// a `reason=expired` query param rather than a distinct route like
// /accept-invite gets, since both this and a normal reset link share the
// exact same /password-reset/confirm path.
function useIsExpiredReason() {
  return useMemo(() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('reason') === 'expired';
  }, []);
}

// Must match apps/api/src/modules/core/routes.js's
// `newPassword: { minLength: 10 }` validation rule exactly -- otherwise a
// password that passes this client-side check can still be rejected
// server-side with a generic-looking VALIDATION_ERROR the user gets no
// specific explanation for.
const MIN_PASSWORD_LENGTH = 10;

export default function PasswordResetConfirmPage({ isInvite = false }) {
  useNamespace('common');
  const { t } = useI18n();
  const token = useResetToken();
  const isExpired = useIsExpiredReason();
  const tenantSlug = useTenantSlugParam();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);

    // Checked client-side first so the (common, easy to hit) case of a too-
    // short password never even makes the round trip -- previously this
    // silently fell through to the generic "Something went wrong" banner,
    // since the server's specific VALIDATION_ERROR response wasn't handled
    // either (fixed below as a fallback, in case this check is ever
    // bypassed or the two length rules ever drift out of sync).
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError({ message: t('common', 'passwordReset.passwordTooShort', { count: MIN_PASSWORD_LENGTH }) });
      return;
    }

    if (newPassword !== confirmPassword) {
      setError({ message: t('common', 'passwordReset.passwordsMustMatch') });
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.post('/auth/password-reset/confirm', { token, newPassword, ...(tenantSlug ? { tenantSlug } : {}) });
      setSubmitted(true);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'INVALID_OR_EXPIRED_TOKEN') {
        setError({ message: t('common', 'passwordReset.invalidOrExpiredToken') });
      } else if (err instanceof ApiError && err.code === 'VALIDATION_ERROR' && err.details?.newPassword) {
        setError({ message: t('common', 'passwordReset.passwordTooShort', { count: MIN_PASSWORD_LENGTH }) });
      } else if (err instanceof ApiError && err.code === 'PASSWORD_PWNED') {
        setError({ message: t('common', 'passwordReset.passwordPwned') });
      } else if (err instanceof ApiError && err.code === 'PASSWORD_REUSED') {
        setError({ message: t('common', 'passwordReset.passwordReused') });
      } else {
        setError({ message: t('common', 'errorBanner') });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <h1 className="auth-card__title">{t('common', 'appName')}</h1>
        <p className="auth-card__subtitle">
          {t('common', isInvite ? 'passwordReset.acceptInviteTitle' : isExpired ? 'passwordReset.expiredTitle' : 'passwordReset.confirmTitle')}
        </p>
        {!isInvite && isExpired && !submitted && (
          <p className="auth-card__hint">{t('common', 'passwordReset.expiredInstructions')}</p>
        )}

        {submitted ? (
          <>
            <p className="success-banner">
              {t(
                'common',
                isInvite
                  ? 'passwordReset.acceptInviteSuccessMessage'
                  : isExpired
                    ? 'passwordReset.expiredSuccessMessage'
                    : 'passwordReset.successConfirmMessage'
              )}
            </p>
            <button type="button" className="btn-link" onClick={() => navigate('/')}>
              {t('common', 'passwordReset.backToLogin')}
            </button>
          </>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            {!token && <div className="error-banner">{t('common', 'passwordReset.invalidOrExpiredToken')}</div>}
            {error && <div className="error-banner">{error.message}</div>}

            <div className="form-field">
              <label className="form-label" htmlFor="reset-new-password">
                {t('common', 'passwordReset.newPasswordLabel')}
              </label>
              <input
                id="reset-new-password"
                className="form-input"
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                required
                minLength={MIN_PASSWORD_LENGTH}
                autoComplete="new-password"
              />
              <span className="form-hint">{t('common', 'passwordReset.passwordHint', { count: MIN_PASSWORD_LENGTH })}</span>
            </div>

            <div className="form-field">
              <label className="form-label" htmlFor="reset-confirm-password">
                {t('common', 'passwordReset.confirmPasswordLabel')}
              </label>
              <input
                id="reset-confirm-password"
                className="form-input"
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                required
                autoComplete="new-password"
              />
            </div>

            <button type="submit" className="btn btn-primary btn-block" disabled={submitting || !token}>
              <KeyRound size={16} /> {t('common', isInvite ? 'passwordReset.acceptInviteSubmitButton' : 'passwordReset.submitConfirm')}
            </button>

            <button type="button" className="btn-link" onClick={() => navigate('/')}>
              {t('common', 'passwordReset.backToLogin')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
